import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForDietitiansPageFr() {
  return (
    <>
      <Head>
        <title>Featcal pour Diététiciens | Meilleur suivi, moins d’admin</title>
        <meta
          name="description"
          content="Featcal aide les diététiciens à structurer le suivi : revue plus rapide des journaux, meilleure adhérence entre séances, et meilleurs résultats (perte de poids, prise de poids, santé métabolique)."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pensé pour les diététiciens qui veulent se concentrer
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Un workflow de suivi répétable pour diététiciens.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal vous fait gagner du temps sur la préparation, les messages
            et l’analyse manuelle-pour vous recentrer sur l’essentiel : le
            raisonnement clinique et le soin.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Commencer gratuitement
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le vrai sujet : le suivi entre les séances
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Les journaux prennent trop de temps à décoder.",
                "Les plans se perdent dans les messages et les PDFs.",
                "L’adhérence baisse entre les séances-et la séance devient du rattrapage.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Revoyez les journaux plus vite",
                desc: "Analyse automatique des journaux + synthèses propres : préparation plus rapide et moins de travail manuel.",
              },
              {
                title: "Moins de no-shows et d’abandons",
                desc: "Rappels et plans dans l’app : plus de régularité entre les séances, sans vous épuiser en relances.",
              },
              {
                title: "Une offre de suivi plus claire",
                desc: "Plans, recettes, progression et guidance au même endroit : le client comprend mieux la valeur du suivi.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                Un workflow centré résultats
              </h2>
              <p className="text-slate-700 leading-relaxed mb-8">
                Featcal garde vos clients réguliers entre les séances et vous
                donne une revue rapide-pour vous concentrer sur les décisions
                cliniques, pas l’admin.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: "1) Onboard en quelques minutes",
                    desc: "Invitez un client, fixez des objectifs et choisissez les suivis. L’app guide au quotidien.",
                  },
                  {
                    title: "2) Revue en minutes",
                    desc: "Tendances et patterns alimentaires en un coup d’œil-sans fouiller dans les captures.",
                  },
                  {
                    title: "3) Des plans qui tiennent",
                    desc: "Plans, recettes et rappels dans l’app-toujours accessibles et à jour.",
                  },
                ].map((s) => (
                  <div
                    key={s.title}
                    className="bg-white border border-slate-200 rounded-2xl p-6"
                  >
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-slate-700 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Les gains rapides
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Préparation plus rapide grâce à des synthèses structurées.",
                  "Plus d’adhérence entre les séances.",
                  "Moins de messages de routine (“je fais quoi ?”).",
                  "Une offre de suivi plus claire à packager et vendre.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Commencer gratuitement
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Idéal pour
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Perte / prise de poids avec revues hebdomadaires.",
                  "Changement d’habitudes (protéines, timing, routines).",
                  "Santé digestive : patterns symptômes + alimentation.",
                  "Nutrition sportive sans chaos Excel.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que Featcal remplace
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Captures, DMs, et notes dispersées.",
                  "Décodage manuel + tableurs.",
                  "Plans perdus et “peux-tu me le renvoyer ?”.",
                  "Relances pour l’adhérence entre les séances.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Plans repas"
          title="Des plans vraiment suivis"
          description="Livrez les plans repas dans l’app : vos clients savent quoi faire, et vous passez moins de temps à renvoyer des PDFs et consignes."
          bullets={[
            "Des plans toujours à jour dans l’app client.",
            "Moins d’allers-retours entre les séances.",
            "Une expérience de suivi plus claire qui améliore la rétention.",
          ]}
          imageSrc="/images/meal-plan.webp"
          imageAlt="Aperçu du plan repas"
          align="right"
          imageFit="contain"
        />
        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Prêt à faire du suivi avec moins d’admin ?
                </h2>
                <p className="text-slate-700 mt-2">
                  Commencez gratuitement et invitez un client en quelques
                  minutes.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Commencer gratuitement
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
